/**
 * ============================================================================
 * [P3-02-C] TrackingTab - 트래킹 탭 메인 컴포넌트
 *
 * 기능:
 * - 날짜 범위 선택
 * - 메트릭 카드 표시
 * - 차트 시각화 (일별/요일별/시간대별)
 * - [P6] 포스트 트래킹 목록 (섹션 토글)
 * ============================================================================
 */

// ============================================================================
// Imports
// ============================================================================
import { useState, useCallback } from 'react'
import { useTrackingData } from '@/hooks/useTrackingData'
import { useTrackingPosts } from '@/hooks/useTrackingPosts'
import { useTexts } from '@/hooks/useTexts'
import { MetricCards } from './MetricCards'
import { DateRangePicker } from './DateRangePicker'
import { SocialMetricsChart } from './SocialMetricsChart'
import { TrackingFilterBar } from './TrackingFilterBar'
import { TrackingPostList } from './TrackingPostList'
import MetricManageModal from './MetricManageModal'  // [P6-01] 메트릭 관리 모달
import type { TrackingPost, TrackingMetric, SavedText } from '@/types'
import styles from './Tracking.module.css'

// ============================================================================
// 섹션 타입
// ============================================================================
type TrackingSection = 'dashboard' | 'posts'

// ============================================================================
// [P3-02-C] TrackingTab Component
// ============================================================================
export function TrackingTab() {
  // ========================================================================
  // 기존 대시보드 데이터
  // ========================================================================
  const {
    metrics,
    isLoading: isDashboardLoading,
    dateRange,
    setDateRange,
  } = useTrackingData()

  // ========================================================================
  // 저장된 글 목록 (내 글 불러오기용)
  // ========================================================================
  const { texts: savedTexts } = useTexts()

  // ========================================================================
  // 포스트 트래킹 데이터
  // ========================================================================
  const {
    posts,
    filteredPosts,
    isLoading: isPostsLoading,
    filter,
    setFilter,
    resetFilter,
    createPost,
    editPost,
    removePost,
    toggleFavorite,
    toggleActive,
    addMetric,
    exportToCSV,
    // [P6-01] 메트릭 관리 관련 함수 추가
    updateMetric,
    deleteMetric,
    batchDeleteMetrics,
  } = useTrackingPosts()

  // ========================================================================
  // 섹션 상태
  // ========================================================================
  const [activeSection, setActiveSection] = useState<TrackingSection>('dashboard')

  // ========================================================================
  // 모달 상태 (포스트 생성/편집, 메트릭 추가)
  // ========================================================================
  const [isPostModalOpen, setIsPostModalOpen] = useState(false)
  const [editingPost, setEditingPost] = useState<TrackingPost | null>(null)
  const [isMetricModalOpen, setIsMetricModalOpen] = useState(false)
  const [metricTargetPostId, setMetricTargetPostId] = useState<string | null>(null)

  // [P6-01] 메트릭 관리 모달 상태
  const [isMetricManageModalOpen, setIsMetricManageModalOpen] = useState(false)
  const [managingPost, setManagingPost] = useState<TrackingPost | null>(null)

  // 폼 상태
  const [postForm, setPostForm] = useState({
    title: '',
    content: '',
    platform: 'threads',
    url: '',
  })

  // [편의기능] 내 글 선택 팝업 & 내용 접기 상태
  const [showSavedTextPicker, setShowSavedTextPicker] = useState(false)
  const [showContentField, setShowContentField] = useState(false)
  const [savedTextSearch, setSavedTextSearch] = useState('')
  const [metricForm, setMetricForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    views: 0,
    likes: 0,
    comments: 0,
    shares: 0,
    follows: 0,
  })

  // ========================================================================
  // 핸들러: 포스트 생성/편집
  // ========================================================================
  const handleOpenCreateModal = useCallback(() => {
    setEditingPost(null)
    setPostForm({ title: '', content: '', platform: 'threads', url: '' })
    setShowSavedTextPicker(false)
    setShowContentField(false)
    setSavedTextSearch('')
    setIsPostModalOpen(true)
  }, [])

  // [편의기능] 저장된 글에서 선택하여 자동 채우기
  const handleSelectSavedText = useCallback((text: SavedText) => {
    // SNS 플랫폼 자동 감지
    const platforms = text.snsPlatforms || []
    let detectedPlatform = 'threads'
    if (platforms.length > 0) {
      const p = platforms[0].toLowerCase()
      if (p.includes('instagram')) detectedPlatform = 'instagram'
      else if (p.includes('twitter') || p.includes('x')) detectedPlatform = 'twitter'
      else if (p.includes('facebook')) detectedPlatform = 'facebook'
      else if (p.includes('youtube')) detectedPlatform = 'youtube'
      else if (p.includes('tiktok')) detectedPlatform = 'tiktok'
      else if (p.includes('threads')) detectedPlatform = 'threads'
    }

    setPostForm({
      title: text.title || text.topic || text.content.substring(0, 30) + '...',
      content: text.content,
      platform: detectedPlatform,
      url: '',
    })
    setShowSavedTextPicker(false)
    setSavedTextSearch('')
  }, [])

  const handleOpenEditModal = useCallback((post: TrackingPost) => {
    setEditingPost(post)
    setPostForm({
      title: post.title,
      content: post.content ?? '',
      platform: post.platform,
      url: post.url ?? '',
    })
    setIsPostModalOpen(true)
  }, [])

  const handleClosePostModal = useCallback(() => {
    setIsPostModalOpen(false)
    setEditingPost(null)
  }, [])

  const handleSubmitPost = useCallback(async () => {
    if (!postForm.title.trim()) {
      alert('제목을 입력해주세요.')
      return
    }

    if (editingPost) {
      // 수정
      await editPost(editingPost.id, postForm)
    } else {
      // 생성
      await createPost({
        ...postForm,
        metrics: [],
      })
    }

    handleClosePostModal()
  }, [postForm, editingPost, createPost, editPost, handleClosePostModal])

  // ========================================================================
  // 핸들러: 메트릭 추가
  // ========================================================================
  const handleOpenMetricModal = useCallback((postId: string) => {
    setMetricTargetPostId(postId)
    setMetricForm({
      date: new Date().toISOString().slice(0, 10),
      views: 0,
      likes: 0,
      comments: 0,
      shares: 0,
      follows: 0,
    })
    setIsMetricModalOpen(true)
  }, [])

  const handleCloseMetricModal = useCallback(() => {
    setIsMetricModalOpen(false)
    setMetricTargetPostId(null)
  }, [])

  const handleSubmitMetric = useCallback(async () => {
    if (!metricTargetPostId) return

    const metricData: Omit<TrackingMetric, 'id'> = {
      date: metricForm.date,
      views: metricForm.views,
      likes: metricForm.likes,
      comments: metricForm.comments,
      shares: metricForm.shares,
      follows: metricForm.follows,
    }

    await addMetric(metricTargetPostId, metricData)
    handleCloseMetricModal()
  }, [metricTargetPostId, metricForm, addMetric, handleCloseMetricModal])

  // ========================================================================
  // [P6-02] 핸들러: 메트릭 관리 모달
  // ========================================================================
  const handleOpenMetricManageModal = useCallback((post: TrackingPost) => {
    setManagingPost(post)
    setIsMetricManageModalOpen(true)
  }, [])

  const handleCloseMetricManageModal = useCallback(() => {
    setIsMetricManageModalOpen(false)
    setManagingPost(null)
  }, [])

  // [P6-02] 메트릭 추가 (모달 내에서)
  const handleAddMetricInModal = useCallback(
    async (metric: Omit<TrackingMetric, 'id'>): Promise<void> => {
      if (!managingPost) return
      await addMetric(managingPost.id, metric)
    },
    [managingPost, addMetric]
  )

  // [P6-02] 메트릭 수정
  const handleUpdateMetricInModal = useCallback(
    async (metricId: string, updates: Partial<TrackingMetric>): Promise<void> => {
      if (!managingPost) return
      await updateMetric(managingPost.id, metricId, updates)
    },
    [managingPost, updateMetric]
  )

  // [P6-02] 메트릭 개별 삭제
  const handleDeleteMetricInModal = useCallback(
    async (metricId: string): Promise<void> => {
      if (!managingPost) return
      await deleteMetric(managingPost.id, metricId)
    },
    [managingPost, deleteMetric]
  )

  // [P6-02] 메트릭 일괄 삭제
  const handleBatchDeleteMetricsInModal = useCallback(
    async (metricIds: string[]): Promise<void> => {
      if (!managingPost || metricIds.length === 0) return
      await batchDeleteMetrics(managingPost.id, metricIds)
    },
    [managingPost, batchDeleteMetrics]
  )

  // ========================================================================
  // 로딩 상태
  // ========================================================================
  if (isDashboardLoading && activeSection === 'dashboard') {
    return (
      <div className={styles.loadingState}>
        <span>데이터를 불러오는 중...</span>
      </div>
    )
  }

  // ========================================================================
  // Render
  // ========================================================================
  return (
    <div className={styles.trackingTab}>
      {/* ================================================================
          섹션 탭
          ================================================================ */}
      <div className={styles.sectionTabs}>
        <button
          type="button"
          className={`${styles.sectionTab} ${activeSection === 'dashboard' ? styles.active : ''}`}
          onClick={() => setActiveSection('dashboard')}
        >
          대시보드
        </button>
        <button
          type="button"
          className={`${styles.sectionTab} ${activeSection === 'posts' ? styles.active : ''}`}
          onClick={() => setActiveSection('posts')}
        >
          포스트 트래킹 ({posts.length})
        </button>
      </div>

      {/* ================================================================
          대시보드 섹션
          ================================================================ */}
      {activeSection === 'dashboard' && (
        <>
          <DateRangePicker
            value={dateRange}
            onChange={setDateRange}
          />
          <MetricCards metrics={metrics} />
          <SocialMetricsChart trackingPosts={posts} />
        </>
      )}

      {/* ================================================================
          포스트 트래킹 섹션
          ================================================================ */}
      {activeSection === 'posts' && (
        <>
          {/* 포스트 추가 버튼 */}
          <div className={styles.postSectionHeader}>
            <h2>포스트 트래킹</h2>
            <button
              type="button"
              onClick={handleOpenCreateModal}
              className={styles.addPostBtn}
            >
              + 포스트 추가
            </button>
          </div>

          {/* 필터 바 */}
          <TrackingFilterBar
            filter={filter}
            onFilterChange={setFilter}
            onResetFilter={resetFilter}
            onExportCSV={exportToCSV}
            totalCount={posts.length}
            filteredCount={filteredPosts.length}
          />

          {/* 포스트 목록 */}
          <TrackingPostList
            posts={filteredPosts}
            isLoading={isPostsLoading}
            onEdit={handleOpenEditModal}
            onDelete={removePost}
            onToggleFavorite={toggleFavorite}
            onToggleActive={toggleActive}
            onAddMetric={handleOpenMetricModal}
            onManageMetrics={handleOpenMetricManageModal}  // [P6-03] 메트릭 관리 모달 열기
          />
        </>
      )}

      {/* ================================================================
          포스트 생성/편집 모달
          ================================================================ */}
      {isPostModalOpen && (
        <div className={styles.modalOverlay} onClick={handleClosePostModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px' }}>
            <h3>{editingPost ? '포스트 편집' : '새 포스트 추가'}</h3>

            {/* ========== 내 글에서 불러오기 (신규 생성 시에만) ========== */}
            {!editingPost && (
              <div style={{ marginBottom: '16px' }}>
                <button
                  type="button"
                  onClick={() => setShowSavedTextPicker(!showSavedTextPicker)}
                  style={{
                    width: '100%', padding: '10px 14px', border: '2px dashed #667eea',
                    borderRadius: '8px', background: showSavedTextPicker ? '#eef2ff' : '#f8faff',
                    color: '#667eea', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem',
                    transition: 'all 0.2s'
                  }}
                >
                  📚 {showSavedTextPicker ? '닫기' : '내 저장된 글에서 불러오기'}
                </button>

                {showSavedTextPicker && (
                  <div style={{
                    marginTop: '8px', border: '1px solid #e2e8f0', borderRadius: '8px',
                    maxHeight: '200px', overflowY: 'auto', background: 'white'
                  }}>
                    <input
                      type="text"
                      value={savedTextSearch}
                      onChange={(e) => setSavedTextSearch(e.target.value)}
                      placeholder="글 검색..."
                      style={{
                        width: '100%', padding: '8px 12px', border: 'none',
                        borderBottom: '1px solid #e2e8f0', fontSize: '0.85rem', boxSizing: 'border-box'
                      }}
                    />
                    {savedTexts
                      .filter(t => t.type !== 'reference')
                      .filter(t => {
                        if (!savedTextSearch.trim()) return true
                        const q = savedTextSearch.toLowerCase()
                        return (t.title?.toLowerCase().includes(q)) ||
                               (t.content?.toLowerCase().includes(q)) ||
                               (t.topic?.toLowerCase().includes(q))
                      })
                      .slice(0, 20)
                      .map(text => (
                        <div
                          key={text.id}
                          onClick={() => handleSelectSavedText(text)}
                          style={{
                            padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9',
                            fontSize: '0.85rem', transition: 'background 0.15s'
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = '#f0f4ff')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'white')}
                        >
                          <div style={{ fontWeight: 600, color: '#1a202c', marginBottom: '2px' }}>
                            {text.title || text.topic || '(제목 없음)'}
                          </div>
                          <div style={{ color: '#718096', fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {text.content.substring(0, 60)}...
                          </div>
                          {text.snsPlatforms && text.snsPlatforms.length > 0 && (
                            <div style={{ marginTop: '2px', fontSize: '0.75rem', color: '#a0aec0' }}>
                              📱 {text.snsPlatforms.join(', ')}
                            </div>
                          )}
                        </div>
                      ))}
                    {savedTexts.filter(t => t.type !== 'reference').length === 0 && (
                      <div style={{ padding: '16px', textAlign: 'center', color: '#a0aec0', fontSize: '0.85rem' }}>
                        저장된 글이 없습니다.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className={styles.formGroup}>
              <label>제목 *</label>
              <input
                type="text"
                value={postForm.title}
                onChange={(e) => setPostForm({ ...postForm, title: e.target.value })}
                placeholder="포스트 제목"
                className={styles.formInput}
              />
            </div>

            {/* ========== 내용 필드 (접기/펼치기) ========== */}
            <div className={styles.formGroup}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <label style={{ margin: 0 }}>내용</label>
                <button
                  type="button"
                  onClick={() => setShowContentField(!showContentField)}
                  style={{
                    background: 'none', border: 'none', color: '#667eea',
                    fontSize: '0.8rem', cursor: 'pointer', padding: '2px 6px'
                  }}
                >
                  {showContentField ? '▲ 접기' : '▼ 펼치기 (선택사항)'}
                </button>
                {postForm.content && !showContentField && (
                  <span style={{ fontSize: '0.75rem', color: '#48bb78' }}>✅ 입력됨</span>
                )}
              </div>
              {showContentField && (
                <textarea
                  value={postForm.content}
                  onChange={(e) => setPostForm({ ...postForm, content: e.target.value })}
                  placeholder="포스트 내용 (선택)"
                  className={styles.formTextarea}
                  rows={3}
                />
              )}
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>플랫폼</label>
                <select
                  value={postForm.platform}
                  onChange={(e) => setPostForm({ ...postForm, platform: e.target.value })}
                  className={styles.formSelect}
                >
                  <option value="threads">Threads</option>
                  <option value="instagram">Instagram</option>
                  <option value="twitter">Twitter/X</option>
                  <option value="facebook">Facebook</option>
                  <option value="youtube">YouTube</option>
                  <option value="tiktok">TikTok</option>
                  <option value="brunch">브런치</option>
                  <option value="blog">네이버 블로그</option>
                  <option value="linkedin">LinkedIn</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label>URL</label>
                <input
                  type="url"
                  value={postForm.url}
                  onChange={(e) => setPostForm({ ...postForm, url: e.target.value })}
                  placeholder="https://..."
                  className={styles.formInput}
                />
              </div>
            </div>

            <div className={styles.modalActions}>
              <button
                type="button"
                onClick={handleClosePostModal}
                className={styles.cancelBtn}
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleSubmitPost}
                className={styles.submitBtn}
              >
                {editingPost ? '수정' : '추가'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================================================================
          메트릭 추가 모달
          ================================================================ */}
      {isMetricModalOpen && (
        <div className={styles.modalOverlay} onClick={handleCloseMetricModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3>메트릭 기록 추가</h3>

            <div className={styles.formGroup}>
              <label>날짜</label>
              <input
                type="date"
                value={metricForm.date}
                onChange={(e) => setMetricForm({ ...metricForm, date: e.target.value })}
                className={styles.formInput}
              />
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>조회수</label>
                <input
                  type="number"
                  value={metricForm.views}
                  onChange={(e) => setMetricForm({ ...metricForm, views: parseInt(e.target.value) || 0 })}
                  className={styles.formInput}
                  min="0"
                />
              </div>
              <div className={styles.formGroup}>
                <label>좋아요</label>
                <input
                  type="number"
                  value={metricForm.likes}
                  onChange={(e) => setMetricForm({ ...metricForm, likes: parseInt(e.target.value) || 0 })}
                  className={styles.formInput}
                  min="0"
                />
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>댓글</label>
                <input
                  type="number"
                  value={metricForm.comments}
                  onChange={(e) => setMetricForm({ ...metricForm, comments: parseInt(e.target.value) || 0 })}
                  className={styles.formInput}
                  min="0"
                />
              </div>
              <div className={styles.formGroup}>
                <label>공유</label>
                <input
                  type="number"
                  value={metricForm.shares}
                  onChange={(e) => setMetricForm({ ...metricForm, shares: parseInt(e.target.value) || 0 })}
                  className={styles.formInput}
                  min="0"
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label>팔로우 (Threads 등)</label>
              <input
                type="number"
                value={metricForm.follows}
                onChange={(e) => setMetricForm({ ...metricForm, follows: parseInt(e.target.value) || 0 })}
                className={styles.formInput}
                min="0"
              />
            </div>

            <div className={styles.modalActions}>
              <button
                type="button"
                onClick={handleCloseMetricModal}
                className={styles.cancelBtn}
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleSubmitMetric}
                className={styles.submitBtn}
              >
                추가
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================================================================
          [P6-04] 메트릭 관리 모달
          ================================================================ */}
      {isMetricManageModalOpen && managingPost && (
        <MetricManageModal
          post={managingPost}
          onClose={handleCloseMetricManageModal}
          onAddMetric={handleAddMetricInModal}
          onUpdateMetric={handleUpdateMetricInModal}
          onDeleteMetric={handleDeleteMetricInModal}
          onBatchDeleteMetrics={handleBatchDeleteMetricsInModal}
        />
      )}
    </div>
  )
}

export default TrackingTab
